'use client';

import { useMemo, useState } from 'react';
import { generateLegalDocument, legalMeta, type LegalVariant } from '@/lib/browser-legal-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserLegalGenerator({ variant }: { variant: LegalVariant }) {
  const meta = useMemo(() => legalMeta[variant], [variant]);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(meta.fields.map((field) => [field.key, field.default])),
  );
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const document = generateLegalDocument(variant, values);

  async function handleCopy() {
    try {
      await copyText(document);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the text.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{meta.badge}</span>
        <h2 className="tool-live-title">{meta.title}</h2>
        <p className="tool-live-subtitle">
          Fill in your details and the document updates live in your browser. This is a practical
          starting template, not legal advice.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {meta.fields.map((field) => (
              <label className="tool-live-field tool-live-field-full" key={field.key}>
                <span>{field.label}</span>
                <input
                  onChange={(event) =>
                    setValues((current) => ({ ...current, [field.key]: event.target.value }))
                  }
                  placeholder={field.placeholder}
                  type="text"
                  value={values[field.key] ?? ''}
                />
              </label>
            ))}
          </div>
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Generated document</span>
            <textarea className="tool-live-preview-frame" readOnly rows={16} value={document} />
          </label>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy document
            </button>
          </div>
          {copyMessage ? (
            <p className="status-message status-success" role="status">
              {copyMessage}
            </p>
          ) : null}
          <p className="tool-live-panel-note">
            Generated locally in your browser. Review the text and adapt it to your needs before
            publishing.
          </p>
        </section>
      </div>
    </div>
  );
}
